'use strict';

// external imports
import React, {
    Component,
    MouseEvent
} from 'react';
import {bindActionCreators} from 'redux';
import {connect} from 'react-redux';
import parse from 'html-react-parser';
import {keys} from 'ts-transformer-keys';

// prime react imports
import {Toolbar as PrimeToolbar} from 'primereact/toolbar';

// shared imports
import {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import User from '~/shared/src/model/User';
import {RequestConfig} from '~/shared/src/utilities/modules/form';

// frontend imports
import Form from '~/frontend/src/components/base/Form';
import {
    AppDispatch,
    StoreState,
    Thunk
} from '~/frontend/src/store';
import {ComponentProps as TargetComponentProps} from '~/frontend/src/modules/panels/FormView';
import {
    getToolbarComponents,
    LayoutComponent,
    layoutComponentTypes,
    ToolbarComponents,
    ToolbarLayoutComponents
} from '~/frontend/src/utilities/toolbars';
import {
    isDataModified,
    isDataModifiedRecursive
} from '~/frontend/src/utilities/dataUtils';
import {getPanelElusiveData} from '~/frontend/src/components/base/page/PageTabPanel/utilities';

// action imports
import closePanel, {ClosePanel} from '~/frontend/src/actions/panelManager/closePanel';
import dataSaveData, {SaveData as DataSaveData} from '~/frontend/src/actions/data/saveData';
import * as PanelActions from '~/frontend/src/actions/panel/common';
import {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import * as FormActions from '~/frontend/src/actions/panel/form';
import {SaveData} from '~/frontend/src/actions/panel/form/saveData';
import {
    load as messageCatalogLoad,
    Load as MessageCatalogLoad
} from '~/frontend/src/actions/messagecatalog';
import {
    setMessage,
    SetMessage
} from '~/frontend/src/actions/intercom';
import xhr from '~/frontend/src/utilities/xhr';
import {fileDownload} from '~/frontend/src/utilities/fileDownload';

// this quirk is needed due to problems with circular dependencies
let saveData = require( '~/frontend/src/actions/panel/form/saveData' ).default;


type SetParam = <K extends keyof TargetComponentProps>( panelKey : string, paramKey : K, paramValue : TargetComponentProps[K] ) => SetParamAction;


export const defaultProps = {
    /**
     * Should the save Button shown in the toolbar <strong>or</strong> not.
     */
    showSaveButton: true as boolean,

    /**
     * The error count to show as badge on save button.
     *
     * Only applicable if <prop>showSaveButton</prop> is set to true.
     */
    saveButtonErrorCount: 0 as number,

    /**
     * The error tooltip to show on save button.
     *
     * Only applicable if <prop>showSaveButton</prop> is set to true AND <prop>saveButtonErrorCount</prop> is set to an positive integer.
     */
    saveButtonErrorTooltip: '' as string | JSX.Element | ( string | JSX.Element )[],

    /**
     * Show copy XML button.
     */
    showCopyXmlButton: true as boolean,

    /**
     * Should the delete Button shown in the toolbar <strong>or</strong> not.
     */
    showDeleteButton: true as boolean,

    /**
     * Show download XML button.
     */
    showDownloadXmlButton: true as boolean,

    /**
     * Show download XML button.
     */
    showDownloadTestTemplateButton: true as boolean,

    showPanelSelectorDropdown: true as boolean,

    /**
     * Show reload button.
     */
    showReloadButton: true as boolean,

    /**
     * Additional buttons to shown on the left side of the toolbar
     */
    leftButtons: [] as ( LayoutComponent | JSX.Element )[],

    /**
     * Additional buttons to shown on the right side of the toolbar
     */
    rightButtons: [] as ( LayoutComponent | JSX.Element )[]
};

export type DefaultProps = typeof defaultProps;

export type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The dataContext to access global.data with.
     */
    dataContext : string;

    /**
     * The database schema to filter data and metadata for.
     */
    schema : Schema;

    /**
     * The Id of the root item. No further root item information required.
     */
    rootItemId? : number;

    viewKey? : string;

    doLoadData : ( reloadConfig : boolean ) => void;

    xmlExportConfig? : RequestConfig;

    /**
     * Event for download XML button
     */
    onDownloadTestTemplateButtonClick? : ( event : MouseEvent<HTMLButtonElement> ) => void;

};

export type ComponentProps = Partial<DefaultProps> & Props;
export const componentProps = keys<ComponentProps>();

type StateProps = {

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;
};

type ActionPropsRaw = {
    closePanel : ClosePanel;

    dataSaveData : DataSaveData;

    // common panel actions
    setParam : SetParam;

    // form actions
    saveData : SaveData;

    // messageCatalog actions
    messageCatalogLoad : MessageCatalogLoad;

    // intercom actions
    setMessage : SetMessage;
};
type ReplaceReturnType<T extends ( ...a : any ) => any, TNewReturn> = ( ...a : Parameters<T> ) => TNewReturn;
type ActionProps = {
    [K in keyof ActionPropsRaw] : ReplaceReturnType<ActionPropsRaw[K], ReturnType<ActionPropsRaw[K]> extends Thunk<any> ? ReturnType<ReturnType<ActionPropsRaw[K]>> : void>;
};

const Actions = Object.assign( { saveData, dataSaveData, closePanel, messageCatalogLoad, setMessage }, PanelActions, FormActions ) as any;

type AllProps = DefaultProps & Props & StateProps & ActionProps;


type State = {
    isItemDeletable : boolean;
};


/**
 * Toolbar
 */
class Toolbar extends Component<AllProps, State> {

    static defaultProps = defaultProps;

    /**
     * @param props
     */
    constructor( props : AllProps ) {
        super( props );

        this.state = {
            isItemDeletable: false
        };

        this.onSaveButtonClick = this.onSaveButtonClick.bind( this );
        this.onCopyXmlButtonClick = this.onCopyXmlButtonClick.bind( this );
        this.onDeleteButtonClick = this.onDeleteButtonClick.bind( this );
        this.onDownloadXmlButtonClick = this.onDownloadXmlButtonClick.bind( this );
        this.onDownloadTestTemplateButtonClick = this.onDownloadTestTemplateButtonClick.bind( this );
    }

    /**
     * REDUX stuff
     */
    static getDerivedStateFromProps( props : AllProps ) : Partial<State> {
        const retVal : Partial<State> = {
                  isItemDeletable: false
              },
              {
                  dataContext,
                  schema,
                  rootItemId,
                  user
              }                       = props,
              rootItem                = rootItemId ? global.data[ dataContext ]?.items[ rootItemId ] : undefined;

        // editable
        if ( rootItem ) {
            const itemTypeId = rootItem.itemTypeId;

            retVal.isItemDeletable = user?.hasPermissionOnItemType( 'delete', itemTypeId, schema ) ?? false;
        }

        if ( Object.keys( retVal ).length ) {
            return retVal;
        }
        else {
            return {};
        }
    }

    /**
     * Handler for save button
     *
     * @private
     */
    onSaveButtonClick() {
        const {
                  uid,
                  dataContext,
                  rootItemId
              } = this.props;

        if ( rootItemId ) {
            void Form.doSaveData( uid, dataContext );
        }
    }

    /**
     * Handler for delete Button click.
     *
     * @private
     */
    onDeleteButtonClick() {
        const {
                  uid,
                  dataContext,
                  rootItemId
              }        = this.props,
              rootItem = rootItemId ? global.data[ dataContext ]?.items[ rootItemId ] : undefined;

        if ( rootItem ) {
            const deleteAndSaveFnc = async () => {
                const deleteAndSaveDataContext = `${dataContext}_deleteAndSave`;

                global.data[ deleteAndSaveDataContext ] = {
                    items     : [],
                    references: [],
                    schema    : rootItem.schema
                };

                rootItem.clone( undefined, false, deleteAndSaveDataContext )
                        .reset()
                    .statusId = metaModel[ rootItem.schema ].getStatus( 'deleted' )!.id;

                const result = await this.props.dataSaveData(
                    deleteAndSaveDataContext,
                    uid,
                    'delete',
                    rootItem.schema
                );

                delete global.data[ deleteAndSaveDataContext ];

                if ( result.success ) {
                    void this.props.closePanel( uid );
                }
            }

            void deleteAndSaveFnc();
        }
    }

    /**
     * Handler for copy XML button
     *
     * @private
     */
    onCopyXmlButtonClick() {
        const {
                  rootItemId,
                  uid,
                  xmlExportConfig
              } = this.props;

        if ( rootItemId && xmlExportConfig?.requestModule ) {

            // show download requested toast
            this.props.setMessage(
                uid,
                'toast',
                'xmlDownloadRequested',
                {
                    severity: 'info',
                    summary : __( 'YN.base.common.xml.downloadRequested.title' ),
                    detail  : parse( __( 'YN.base.common.xml.downloadRequested.text' ) ),
                    closable: true,
                    sticky  : true
                }
            );

            // request data
            const doExport = async () => {
                const fileContent = await this.getExportXml();

                if ( fileContent ) {
                    await navigator.clipboard.writeText( fileContent );

                    this.props.setMessage(
                        uid,
                        'toast',
                        'xmlDataCopiedToClipboard',
                        {
                            severity: 'info',
                            summary : __( 'YN.base.common.xml.dataCopiedToClipboard.title' ),
                            detail  : __( 'YN.base.common.xml.dataCopiedToClipboard.text' )
                        }
                    );
                }
            };

            void doExport();
        }
    }

    /**
     * Handler for download XML button
     *
     * @private
     */
    onDownloadXmlButtonClick() {
        const {
                  dataContext,
                  rootItemId,
                  uid,
                  xmlExportConfig
              } = this.props;

        if ( rootItemId && xmlExportConfig?.requestModule ) {

            // show download requested toast
            this.props.setMessage(
                uid,
                'toast',
                'xmlDownloadRequested',
                {
                    severity: 'info',
                    summary : __( 'YN.base.common.xml.downloadRequested.title' ),
                    detail  : parse( __( 'YN.base.common.xml.downloadRequested.text' ) ),
                    closable: true,
                    sticky  : true
                }
            );

            // request data
            const doExport = async () => {
                const fileContent = await this.getExportXml();

                if ( fileContent ) {
                    fileDownload(
                        fileContent,
                        `${global.data[ dataContext ]?.items[ rootItemId ]?.itemType.name}_${global.data[ dataContext ]?.items[ rootItemId ]?.key}`,
                        'xml'
                    );

                    this.props.setMessage(
                        uid,
                        'toast',
                        'xmlDownloadComplete',
                        {
                            severity: 'success',
                            summary : __( 'YN.base.common.xml.downloadComplete.title' ),
                            detail  : parse( __( 'YN.base.common.xml.downloadComplete.text' ) )
                        }
                    );
                }
            };

            void doExport();
        }
    }

    async getExportXml() {
        const {
                  dataContext,
                  rootItemId,
                  schema,
                  uid,
                  xmlExportConfig
              } = this.props;

        let retVal : string | undefined = undefined;

        if ( xmlExportConfig ) {
            const elusiveData                                            = getPanelElusiveData( uid ?? '' ),
                  processedRequestParams : { [ key : string ] : string } = Form.resolveRequestParameters(
                      xmlExportConfig.requestParams,
                      schema,
                      dataContext,
                      rootItemId!,
                      elusiveData?.variables
                  ),
                  response                                               = await xhr.post(
                      `${BASE_URL}/api/${xmlExportConfig.requestModule}${xmlExportConfig.requestCommand ? `/${xmlExportConfig.requestCommand}` : ''}`,
                      processedRequestParams,
                      undefined,
                      `export_xml_for_${rootItemId}`,
                      'loadXmlExportData'
                  );

            if ( response?.data ) {
                retVal = response.data.data as string;
            }
        }

        return retVal;
    }

    /**
     * Handler for download test template button
     *
     * @private
     */
    onDownloadTestTemplateButtonClick( event : MouseEvent<HTMLButtonElement> ) {
        if ( this.props.onDownloadTestTemplateButtonClick ) {
            this.props.onDownloadTestTemplateButtonClick( event );
        }
    }

    /**
     * Handler for reload button
     *
     * @private
     */
    onReloadButtonClick( reloadConfig : boolean ) {
        if ( reloadConfig ) {
            void this.props.messageCatalogLoad();
        }
        this.props.doLoadData( reloadConfig );
    }

    /**
     * REACT lifecycle method
     */
    render() {
        const {
                  uid,
                  dataContext,
                  schema,
                  rootItemId,
                  showSaveButton,
                  saveButtonErrorCount,
                  saveButtonErrorTooltip,
                  showCopyXmlButton,
                  showDeleteButton,
                  showDownloadXmlButton,
                  showDownloadTestTemplateButton,
                  showPanelSelectorDropdown,
                  showReloadButton,
                  leftButtons,
                  rightButtons,
                  viewKey
              }                                          = this.props,
              { isItemDeletable }                        = this.state,
              layoutComponents : ToolbarLayoutComponents = {
                  left : [],
                  right: []
              };

        // left
        if ( showSaveButton ) {
            layoutComponents.left.push( {
                type  : 'saveButton',
                saveButtonErrorCount,
                saveButtonErrorTooltip,
                params: {
                    key: `${uid}_saveButton`,

                    onClick: this.onSaveButtonClick
                }
            } );
        }

        if ( showDeleteButton ) {
            layoutComponents.left.push( {
                type  : 'deleteButton',
                params: {
                    key: `${uid}_deleteButton`,

                    disabled: !isItemDeletable,
                    confirm : true,
                    onClick : this.onDeleteButtonClick
                }
            } );
        }

        layoutComponents.left.push( {
            type  : 'separator',
            params: {
                className: 'yn-responsive-hidden'
            }
        } );

        if ( showCopyXmlButton ) {
            layoutComponents.left.push( {
                type  : 'button',
                params: {
                    key: `${uid}_copyXmlButton`,

                    icon     : 'pi pi-copy',
                    tooltip  : __( 'YN.base.common.copyXml' ),
                    onClick  : this.onCopyXmlButtonClick,
                    className: 'p-button-text yn-copy-xml-button yn-responsive-hidden'
                }
            } );
        }

        if ( showDownloadXmlButton ) {
            layoutComponents.left.push( {
                type  : 'button',
                params: {
                    key: `${uid}_downloadXmlButton`,

                    icon     : 'pi pi-download',
                    tooltip  : __( 'YN.base.common.downloadXml' ),
                    onClick  : this.onDownloadXmlButtonClick,
                    className: 'p-button-text yn-download-xml-button yn-responsive-hidden'
                }
            } );
        }

        if ( showDownloadTestTemplateButton ) {
            layoutComponents.left.push( {
                type  : 'button',
                params: {
                    key: `${uid}_downloadTestTemplate`,

                    icon     : 'fas fa-flask',
                    tooltip  : __( 'YN.base.downloadTestTemplate.button' ),
                    onClick  : this.onDownloadTestTemplateButtonClick,
                    className: 'p-button-text yn-download-button yn-responsive-hidden'
                }
            } );
        }

        layoutComponents.left.push( {
            type  : 'separator',
            params: {
                className: 'yn-responsive-hidden'
            }
        } );

        if ( leftButtons.length ) {
            layoutComponents.left = layoutComponents.left.concat(
                leftButtons.map( ( component, index ) => {
                    if ( component.type && layoutComponentTypes.includes( component.type ) ) {
                        return {
                            type  : component.type,
                            params: {
                                key: `leftButton_${index}`,
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                // @ts-ignore
                                ...( component.params ?? {} )
                            }
                        } as LayoutComponent
                    }
                    else {
                        return {
                            type  : 'custom',
                            params: {
                                key: `leftButton_${index}`,
                                component
                            }
                        } as LayoutComponent
                    }
                } )
            );
        }

        // right
        if ( showPanelSelectorDropdown ) {
            const item       = rootItemId
                      ? global.data[ dataContext ]?.items[ rootItemId ]
                      : undefined,
                  itemParams = item
                      ? {
                          schema  : item.schema,
                          itemId  : item.id,
                          itemType: item.itemTypeId
                      }
                      : {};

            layoutComponents.right.push( {
                type  : 'panelSelectorDropdown',
                params: {
                    key: `${uid}_panelSelector`,

                    uid        : `${uid}_panelSelector`,
                    labeled    : true,
                    className  : 'yn-responsive-hidden',
                    style      : { width: '18rem' },
                    currentView: `Form:${viewKey}`,
                    ...itemParams
                }
            } );
        }

        if ( rightButtons.length ) {
            layoutComponents.right = layoutComponents.right.concat(
                rightButtons.map( ( component, index ) => {
                    if ( component.type && layoutComponentTypes.includes( component.type ) ) {
                        return {
                            type  : component.type,
                            params: {
                                key: `rightButton_${index}`,
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                // @ts-ignore
                                ...( component.params ?? {} )
                            }
                        } as LayoutComponent
                    }
                    else {
                        return {
                            type  : 'custom',
                            params: {
                                key: `rightButton_${index}`,
                                component
                            }
                        } as LayoutComponent
                    }
                } )
            );
        }

        layoutComponents.right.push( {
            type  : 'separator',
            params: {
                className: 'yn-responsive-hidden'
            }
        } );

        if ( showReloadButton ) {
            layoutComponents.right.push( {
                type  : 'reloadButton',
                params: {
                    key: `${uid}_reloadButton`,

                    confirm: () => rootItemId ? isDataModifiedRecursive( dataContext, schema, rootItemId ) : isDataModified( dataContext, schema ),
                    onClick: this.onReloadButtonClick.bind( this, true )
                }
            } );
        }

        const toolbarComponents : ToolbarComponents = getToolbarComponents( layoutComponents );

        return <PrimeToolbar
            start={toolbarComponents.left}
            end={toolbarComponents.right}
            className="yn-panel-toolbar shadow-4"
            style={{ zIndex: 5 }}
        />;
    }
}

/**
 * REDUX stuff
 */
function mapStateToProps( state : StoreState ) : StateProps {
    const retVal : StateProps = {
        user: undefined
    };

    // get some session user information
    if ( state.session?.user ) {
        retVal.user = state.session.user;
    }

    return retVal;
}

/**
 * REDUX stuff
 */
function mapDispatchToProps( dispatch : AppDispatch ) {
    // this quirk is needed due to problems with circular dependencies
    // TODO: switch to target component action (FormView or so)...
    saveData = require( '~/frontend/src/actions/panel/treeView/saveData' ).default;
    return bindActionCreators( Actions, dispatch );
}

export default connect<StateProps, ActionProps, ComponentProps, StoreState>( mapStateToProps, mapDispatchToProps )( Toolbar );
